import React, { useState, useCallback } from 'react';
import { Header } from '@/components/Header';
import { ContextToggle } from '@/components/ContextToggle';
import { Wheel } from '@/components/Wheel';
import { CategoryResultCard } from '@/components/CategoryResultCard';
import { Toast } from '@/components/Toast';
import { Footer } from '@/components/Footer';
import { usePowerSkillsStorage } from '@/hooks/useLocalStorage';
import { useSkillRotation } from '@/hooks/useSkillRotation';

export const WheelPage = () => {
  const {
    todaySkillId,
    todayContext,
    todayUsed,
    hasSpunToday,
    recordSpin,
    updateContext,
    markAsUsed
  } = usePowerSkillsStorage();

  const { getNextSkill } = useSkillRotation();

  // Initialize from localStorage — but ONLY if the user has spun today.
  // Otherwise clear stale results from a previous day.
  const [currentCategory, setCurrentCategory] = useState(() => {
    try {
      const lastDate = localStorage.getItem('psw_lastSpinDate');
      const today = new Date().toISOString().split('T')[0];
      if (lastDate === today) {
        return localStorage.getItem('psw_todayCategory') || null;
      }
      return null;
    } catch {
      return null;
    }
  });
  
  const [currentSkillName, setCurrentSkillName] = useState(() => {
    try {
      const lastDate = localStorage.getItem('psw_lastSpinDate');
      const today = new Date().toISOString().split('T')[0];
      if (lastDate === today) {
        return localStorage.getItem('psw_todaySkillName') || null;
      }
      return null;
    } catch {
      return null;
    }
  });
  
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const handleSpinComplete = useCallback((category) => {
    // Get the next skill for this category (rotation logic)
    const skillName = getNextSkill(category);
    
    setCurrentCategory(category);
    setCurrentSkillName(skillName);
    
    // Save to localStorage
    try {
      localStorage.setItem('psw_todayCategory', category);
      localStorage.setItem('psw_todaySkillName', skillName);
    } catch (e) {
      console.error('Failed to save result:', e);
    }
    
    // Record the spin (for daily limit)
    recordSpin(skillName);
  }, [getNextSkill, recordSpin]);

  const handleContextChange = useCallback((newContext) => {
    updateContext(newContext);
  }, [updateContext]);

  const handleMarkUsed = useCallback(() => {
    markAsUsed();
    setToastMessage('Marked as done');
    setShowToast(true);
  }, [markAsUsed]);

  const handleCopyShare = useCallback(() => {
    setToastMessage('Copied');
    setShowToast(true);
  }, []);

  const hideToast = useCallback(() => {
    setShowToast(false);
  }, []);

  const spunToday = hasSpunToday();

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="flex-1 w-full max-w-2xl mx-auto px-4 py-6 sm:py-10">
        {/* Header */}
        <Header />

        {/* Context Toggle */}
        <div className="mb-6">
          <ContextToggle 
            value={todayContext} 
            onChange={handleContextChange} 
          />
        </div>

        {/* Wheel Section - increased bottom margin for spacing */}
        <div className="flex justify-center mb-12">
          <Wheel 
            onSpinComplete={handleSpinComplete}
            disabled={spunToday}
            hasSpunToday={spunToday}
          />
        </div>

        {/* Result Card - the main payoff */}
        {currentCategory && currentSkillName && (
          <div className="w-full max-w-lg mx-auto mb-10 card-animated">
            <CategoryResultCard 
              category={currentCategory}
              skillName={currentSkillName}
              context={todayContext}
              isUsed={todayUsed}
              onMarkUsed={handleMarkUsed}
              onCopyShare={handleCopyShare}
            />
          </div>
        )}

        {/* Toast */}
        <Toast 
          message={toastMessage}
          show={showToast}
          onHide={hideToast}
        />

        {/* Footer */}
        <Footer />
      </div>
    </div>
  );
};

export default WheelPage;
